/* ==========================================================================
   FILE: modules/auth/components/RegisterForm.jsx
   IAMONEAI - Multi-Step Registration Form
   Handles email verification and identity creation
   ========================================================================== */

import React, { useState, useCallback, useEffect } from 'react';
import { Button, Input } from '../../../global/components';
import { useAPI } from '../../../global/hooks/useAPI';
import { 
  validateEmail, 
  validateName, 
  validateBirthDate,
  validateWallet,
  isDisposableEmail 
} from './validation';
import './RegisterForm.css';

// ==========================================================================
// CONSTANTS
// ==========================================================================

const STEPS = {
  EMAIL: 0,
  VERIFY: 1,
  PROFILE: 2,
  COMPLETE: 3,
};

const STEP_LABELS = ['Email', 'Verify', 'Profile', 'Complete'];

// ==========================================================================
// REGISTER FORM COMPONENT
// ==========================================================================

/**
 * Multi-step registration form
 * 
 * Steps:
 * 1. Email input + reCAPTCHA
 * 2. Email verification code
 * 3. Profile information (name, birthdate, wallet)
 * 4. Success with IIN display
 * 
 * @param {Object} props
 * @param {Function} onSuccess - Called with user data on successful registration
 * @param {Function} onLoginClick - Navigate to login page
 * @param {string} recaptchaSiteKey - Google reCAPTCHA site key
 */
function RegisterForm({
  onSuccess,
  onLoginClick,
  recaptchaSiteKey,
  className = '',
}) {
  // Step state
  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    wallet: '',
  });

  // UI state
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [createdIIN, setCreatedIIN] = useState('');

  // API hook
  const api = useAPI();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Update form field
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setMessage({ type: '', text: '' });
  }, []);

  // ==========================================================================
  // STEP 1: EMAIL SUBMISSION
  // ==========================================================================

  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const { email } = formData;

    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setErrors({ email: emailValidation.error });
      return;
    }

    // Check for disposable email
    if (isDisposableEmail(email)) {
      setErrors({ email: 'Please use a permanent email address, not a temporary one.' });
      return;
    }

    setIsLoading(true);

    try {
      // Get reCAPTCHA token if available
      let recaptchaToken = null;
      if (window.grecaptcha && recaptchaSiteKey) {
        recaptchaToken = await window.grecaptcha.execute(recaptchaSiteKey, { action: 'register' });
      }

      // Send verification code
      const response = await api.sendVerificationCode(email, recaptchaToken);

      if (response.success) {
        setCurrentStep(STEPS.VERIFY);
        setResendCooldown(60);
        setMessage({ type: 'success', text: 'Verification code sent! Check your email.' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to send code. Please try again.' });
      }
    } catch (err) {
      console.error('Send code error:', err);
      setMessage({ type: 'error', text: 'Failed to send verification code. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  }, [formData, api, recaptchaSiteKey]);

  // ==========================================================================
  // STEP 2: CODE VERIFICATION
  // ==========================================================================

  const handleVerifySubmit = useCallback(async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const { email, verificationCode } = formData;

    if (!verificationCode || verificationCode.length !== 6) {
      setErrors({ verificationCode: 'Please enter the 6-digit code' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.verifyCode(email, verificationCode);

      if (response.success || response.verified) {
        setCurrentStep(STEPS.PROFILE);
        setMessage({ type: 'success', text: 'Email verified! Now complete your profile.' });
      } else {
        setErrors({ verificationCode: response.message || 'Invalid code. Please try again.' });
      }
    } catch (err) {
      console.error('Verify code error:', err);
      setErrors({ verificationCode: 'Verification failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  }, [formData, api]);

  // Resend verification code
  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let recaptchaToken = null;
      if (window.grecaptcha && recaptchaSiteKey) {
        recaptchaToken = await window.grecaptcha.execute(recaptchaSiteKey, { action: 'resend' });
      }

      const response = await api.sendVerificationCode(formData.email, recaptchaToken);

      if (response.success) {
        setResendCooldown(60);
        setMessage({ type: 'success', text: 'New code sent!' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to resend code.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to resend code. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  }, [formData.email, resendCooldown, api, recaptchaSiteKey]);

  // ==========================================================================
  // STEP 3: PROFILE SUBMISSION
  // ==========================================================================

  const handleProfileSubmit = useCallback(async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const { firstName, lastName, birthDate, wallet, email } = formData;
    const newErrors = {};

    // Validate all fields
    const firstNameValidation = validateName(firstName, 'First name');
    if (!firstNameValidation.valid) newErrors.firstName = firstNameValidation.error;

    const lastNameValidation = validateName(lastName, 'Last name');
    if (!lastNameValidation.valid) newErrors.lastName = lastNameValidation.error;

    const birthDateValidation = validateBirthDate(birthDate);
    if (!birthDateValidation.valid) newErrors.birthDate = birthDateValidation.error;

    // Wallet is optional but validate if provided
    if (wallet) {
      const walletValidation = validateWallet(wallet);
      if (!walletValidation.valid) newErrors.wallet = walletValidation.error;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      // Register identity
      const response = await api.register({
        firstName,
        lastName,
        birthDate,
        wallet: wallet || undefined,
        email,
      });

      if (response.success && response.iin) {
        setCreatedIIN(response.iin);
        setCurrentStep(STEPS.COMPLETE);
        setMessage({ type: 'success', text: 'Identity created successfully!' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Registration failed. Please try again.' });
      }
    } catch (err) {
      console.error('Registration error:', err);
      setMessage({ type: 'error', text: 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  }, [formData, api]);

  // ==========================================================================
  // STEP 4: COMPLETION
  // ==========================================================================

  const handleCopyIIN = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(createdIIN);
      setMessage({ type: 'success', text: 'IIN copied to clipboard!' });
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [createdIIN]);

  const handleComplete = useCallback(() => {
    onSuccess?.({
      iin: createdIIN,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
    });
  }, [createdIIN, formData, onSuccess]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const formClasses = [
    'register-form',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={formClasses}>
      {/* Step Indicator */}
      <div className="register-form-steps">
        {STEP_LABELS.map((label, index) => (
          <div 
            key={label}
            className={`register-form-step-indicator ${index <= currentStep ? 'register-form-step-active' : ''} ${index < currentStep ? 'register-form-step-complete' : ''}`}
          >
            <div className="register-form-step-number">
              {index < currentStep ? '✓' : index + 1}
            </div>
            <span className="register-form-step-label">{label}</span>
          </div>
        ))}
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`register-form-message register-form-message-${message.type}`}>
          <span>{message.text}</span>
          <button type="button" onClick={() => setMessage({ type: '', text: '' })}>×</button>
        </div>
      )}

      {/* Step 1: Email */}
      {currentStep === STEPS.EMAIL && (
        <form onSubmit={handleEmailSubmit} className="register-form-step">
          <div className="register-form-header">
            <h2 className="register-form-title">Create Your Identity</h2>
            <p className="register-form-subtitle">
              Enter your email to get started with IAMONEAI
            </p>
          </div>

          <Input
            label="Email Address"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            error={errors.email}
            disabled={isLoading}
            autoComplete="email"
            autoFocus
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={!formData.email}
          >
            Send Verification Code
          </Button>

          <button
            type="button"
            className="register-form-link"
            onClick={onLoginClick}
          >
            Already have an account? Sign in
          </button>
        </form>
      )}

      {/* Step 2: Verify Code */}
      {currentStep === STEPS.VERIFY && (
        <form onSubmit={handleVerifySubmit} className="register-form-step">
          <div className="register-form-header">
            <h2 className="register-form-title">Check Your Email</h2>
            <p className="register-form-subtitle">
              We sent a 6-digit code to <strong>{formData.email}</strong>
            </p>
          </div>

          <Input
            label="Verification Code"
            type="text"
            placeholder="000000"
            value={formData.verificationCode}
            onChange={(e) => updateField('verificationCode', e.target.value.replace(/\D/g, '').slice(0, 6))}
            error={errors.verificationCode}
            disabled={isLoading}
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            className="register-form-code-input"
            autoFocus
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={formData.verificationCode.length !== 6}
          >
            Verify Code
          </Button>

          <div className="register-form-resend">
            <span>Didn't receive the code?</span>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || isLoading}
              className="register-form-resend-btn"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </button>
          </div>

          <button
            type="button"
            className="register-form-back"
            onClick={() => setCurrentStep(STEPS.EMAIL)}
            disabled={isLoading}
          >
            ← Change email address
          </button>
        </form>
      )}

      {/* Step 3: Profile */}
      {currentStep === STEPS.PROFILE && (
        <form onSubmit={handleProfileSubmit} className="register-form-step">
          <div className="register-form-header">
            <h2 className="register-form-title">Complete Your Profile</h2>
            <p className="register-form-subtitle">
              This information creates your unique identity
            </p>
          </div>

          <div className="register-form-row">
            <Input
              label="First Name"
              type="text"
              placeholder="John"
              value={formData.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              error={errors.firstName}
              disabled={isLoading}
              autoComplete="given-name"
              autoFocus
            />

            <Input
              label="Last Name"
              type="text"
              placeholder="Doe"
              value={formData.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              error={errors.lastName}
              disabled={isLoading}
              autoComplete="family-name"
            />
          </div>

          <Input
            label="Birth Date"
            type="date"
            value={formData.birthDate}
            onChange={(e) => updateField('birthDate', e.target.value)}
            error={errors.birthDate}
            disabled={isLoading}
          />

          <Input
            label="Wallet Address (Optional)"
            type="text"
            placeholder="0x..."
            value={formData.wallet}
            onChange={(e) => updateField('wallet', e.target.value)}
            error={errors.wallet}
            disabled={isLoading}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={!formData.firstName || !formData.lastName || !formData.birthDate}
          >
            Create My Identity
          </Button>
        </form>
      )}

      {/* Step 4: Complete */}
      {currentStep === STEPS.COMPLETE && (
        <div className="register-form-step register-form-complete">
          <div className="register-form-success-icon">
            <CheckCircleIcon />
          </div>

          <div className="register-form-header">
            <h2 className="register-form-title">Welcome to IAMONEAI!</h2>
            <p className="register-form-subtitle">
              Your AI guardian is ready. Save your IIN - it's your key to access.
            </p>
          </div>

          <div className="register-form-iin-display">
            <span className="register-form-iin-label">Your IIN</span>
            <span className="register-form-iin-value">{createdIIN}</span>
            <button
              type="button"
              className="register-form-copy-btn"
              onClick={handleCopyIIN}
              aria-label="Copy IIN"
            >
              <CopyIcon />
            </button>
          </div>

          <div className="register-form-warning">
            <WarningIcon />
            <span>Save this IIN securely. You'll need it to sign in.</span>
          </div>

          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleComplete}
          >
            Start Using IAMONEAI
          </Button>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// ICONS
// ==========================================================================

function CheckCircleIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default RegisterForm;